import React, { useId } from 'react'
import type { PersonFinancialFootprint as PersonFinancialFootprintData } from '../../../types'
import { Tooltip } from '../../ui'
import {
  formatPersonFootprintTooltip,
  type PersonFootprintTooltipKind,
} from '../../../utils/financial'
import FinancialScaleDots from '../financial/FinancialScaleDots'

interface PersonFinancialFootprintProps {
  footprint: PersonFinancialFootprintData | null
  personName: string
  loading?: boolean
}

const SCALE_LABELS = {
  ejerskab: 'Ejerskab',
  indflydelse: 'Bestyrelse',
} as const

function TooltipBody({ body, note }: { body: string; note: string | null }) {
  return (
    <>
      <span>{body}</span>
      {note && (
        <span className="mt-1.5 block text-xs text-ink-500 leading-snug">{note}</span>
      )}
    </>
  )
}

function ScaleItem({
  kind,
  shortLabel,
  personName,
  bucket,
  testId,
}: {
  kind: PersonFootprintTooltipKind
  shortLabel: string
  personName: string
  bucket: PersonFinancialFootprintData['ejerskab']
  testId: string
}) {
  const tooltipId = useId()
  const hasData = bucket.companiesWithData > 0
  const scaleValue = hasData ? bucket.scale : null
  const tooltip = formatPersonFootprintTooltip(kind, personName, bucket)

  return (
    <Tooltip
      id={tooltipId}
      content={<TooltipBody body={tooltip.body} note={tooltip.note} />}
    >
      <span data-testid={testId} className="inline-flex items-center gap-2 min-w-0 cursor-default">
        <span className="text-ink-500 shrink-0">{shortLabel}</span>
        <FinancialScaleDots
          value={scaleValue}
          testId={`${testId}-scale`}
          ariaLabel={
            hasData
              ? `${shortLabel}: skala ${bucket.scale} af 5`
              : `${shortLabel}: ingen data`
          }
        />
      </span>
    </Tooltip>
  )
}

/**
 * Compact one-line revenue scale for the person profile header.
 */
const PersonFinancialFootprint: React.FC<PersonFinancialFootprintProps> = ({
  footprint,
  personName,
  loading = false,
}) => {
  if (loading) {
    return (
      <p className="mt-2 text-sm text-ink-400" data-testid="person-financial-footprint-loading">
        Beregner økonomisk omfang…
      </p>
    )
  }

  if (!footprint) {
    return null
  }

  const hasAny =
    footprint.ejerskab.companyCount > 0 || footprint.indflydelse.companyCount > 0
  if (!hasAny) {
    return null
  }

  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-700"
      data-testid="person-financial-footprint"
    >
      {footprint.ejerskab.companyCount > 0 && (
        <ScaleItem
          kind="ejerskab"
          shortLabel={SCALE_LABELS.ejerskab}
          personName={personName}
          bucket={footprint.ejerskab}
          testId="person-financial-ejerskab"
        />
      )}
      {footprint.indflydelse.companyCount > 0 && (
        <ScaleItem
          kind="indflydelse"
          shortLabel={SCALE_LABELS.indflydelse}
          personName={personName}
          bucket={footprint.indflydelse}
          testId="person-financial-indflydelse"
        />
      )}
    </div>
  )
}

export default PersonFinancialFootprint
